// Halal filter utility for Arabic-speaking users
// Filters out haram (forbidden) ingredients from recipes and menus

// List of haram ingredients (case-insensitive matching)
const HARAM_INGREDIENTS = [
    // Pork and pork products
    'pork', 'bacon', 'ham', 'sausage', 'prosciutto', 'pancetta', 'salami', 'pepperoni',
    'chorizo', 'lard', 'pork chop', 'pork belly', 'pork ribs', 'pork loin',
    'خنزير', 'لحم خنزير', 'بيكون', 'لحم مقدد',
    
    // Alcohol and alcoholic beverages
    'wine', 'beer', 'vodka', 'whiskey', 'whisky', 'rum', 'gin', 'tequila', 'sake',
    'brandy', 'cognac', 'champagne', 'liqueur', 'alcohol', 'alcoholic',
    'sherry', 'port', 'vermouth', 'bourbon', 'scotch',
    'red wine', 'white wine', 'cooking wine', 'marsala', 'mirin',
    'نبيذ', 'خمر', 'كحول', 'بيرة', 'فودكا', 'ويسكي',
    
    // Gelatin (if from pork)
    'gelatin', 'gelatine', 'جيلاتين',
    
    // Other potentially haram items
    'lard', 'shortening' // may contain pork fat
];

// Check if a language is Arabic-speaking (including all Arabic country variants)
export const isArabicLanguage = (language) => {
    const arabicLanguages = [
        'arabic', 
        'bahrain-arabic', 'bh-ar', 'bahrain-english', 'bh-en',
        'uae-arabic', 'uae-english',
        'sa-arabic', 'sa-english',
        'kw-arabic', 'kw-english',
        'qa-arabic', 'qa-english',
        'om-arabic', 'om-english',
        'iq-arabic', 'iq-english',
        'jo-arabic', 'jo-english',
        'lb-arabic', 'lb-english',
        'sy-arabic', 'sy-english',
        'ps-arabic', 'ps-english',
        'ye-arabic', 'ye-english',
        'eg-arabic', 'eg-english'
    ];
    return arabicLanguages.includes(language.toLowerCase());
};

// Check if an ingredient name contains haram items
export const containsHaram = (ingredientName) => {
    if (!ingredientName) return false;
    
    const lowerName = ingredientName.toLowerCase();
    
    return HARAM_INGREDIENTS.some(haram => {
        const lowerHaram = haram.toLowerCase();
        // Check for whole word matches or as part of compound words
        return lowerName.includes(lowerHaram);
    });
};

// Filter out recipes that contain haram ingredients
export const filterHalalRecipes = (recipes, language) => {
    if (!isArabicLanguage(language)) {
        return recipes; // No filtering for non-Arabic languages
    }
    
    return recipes.filter(recipe => {
        // Check recipe name
        if (containsHaram(recipe.name || recipe.strMeal)) {
            return false;
        }
        
        // Check recipe category
        if (containsHaram(recipe.category || recipe.strCategory)) {
            return false;
        }
        
        // Check ingredients
        if (recipe.ingredients) {
            if (typeof recipe.ingredients === 'string') {
                if (containsHaram(recipe.ingredients)) {
                    return false;
                }
            } else if (Array.isArray(recipe.ingredients)) {
                const hasHaram = recipe.ingredients.some(ing => 
                    containsHaram(typeof ing === 'string' ? ing : ing.name || ing.ingredient)
                );
                if (hasHaram) {
                    return false;
                }
            }
        }
        
        // Check individual ingredient fields (strIngredient1, strIngredient2, etc.)
        for (let i = 1; i <= 20; i++) {
            const ingredientKey = `strIngredient${i}`;
            if (recipe[ingredientKey] && containsHaram(recipe[ingredientKey])) {
                return false;
            }
        }
        
        // Check tags
        if (recipe.tags || recipe.strTags) {
            const tags = recipe.tags || recipe.strTags;
            if (containsHaram(tags)) {
                return false;
            }
        }
        
        return true; // Recipe is halal
    });
};

// Filter ingredient options for dropdowns/menus
export const filterHalalIngredients = (ingredients, language) => {
    if (!isArabicLanguage(language)) {
        return ingredients; // No filtering for non-Arabic languages
    }
    
    return ingredients.filter(ingredient => {
        const name = ingredient.strIngredient || ingredient.name || ingredient.value || ingredient.label || ingredient;
        return !containsHaram(name);
    });
};

// Filter areas/cuisines that commonly use haram ingredients
const HARAM_CUISINES = ['pork', 'alcoholic'];

export const filterHalalAreas = (areas, language) => {
    if (!isArabicLanguage(language)) {
        return areas;
    }
    
    return areas.filter(area => {
        const name = area.strArea || area.name || area;
        return !HARAM_CUISINES.some(haram => name.toLowerCase().includes(haram));
    });
};

export default {
    isArabicLanguage,
    containsHaram,
    filterHalalRecipes,
    filterHalalIngredients,
    filterHalalAreas
};

